import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  TrendingUp, Search, Target, BarChart3, Share2, Award, 
  ArrowRight, Sparkles, Rocket, Calendar, MessageSquare, MapPin,
  Linkedin, Video, Store, Zap, CheckCircle2
} from "lucide-react";
import { 
  SiGoogleanalytics, SiMeta, SiGooglesearchconsole, 
  SiSemrush, SiHubspot, SiGoogletagmanager 
} from "react-icons/si";

export default function DigitalMarketingService() {
  const capabilities = [
    {
      icon: <Search className="text-blue-600 dark:text-cyan-400" size={26} />,
      title: "Technical & On-Page SEO",
      desc: "Optimizing website architecture, search intent keywords, meta tags, schema data, and backlink authority to dominate search rankings.",
      highlights: ["Technical Audit", "JSON-LD Schema", "Backlink Authority"]
    },
    {
      icon: <Target className="text-indigo-600 dark:text-violet-400" size={26} />,
      title: "Performance Ads (Google & Meta)",
      desc: "High-ROI PPC campaigns across Google Search, Display, Meta Ads (Facebook & Instagram), targeting high-intent buyer audiences.",
      highlights: ["Google Search Ads", "Meta Conversion Ads", "High ROI Funnels"]
    },
    {
      icon: <Share2 className="text-cyan-600 dark:text-cyan-300" size={26} />,
      title: "Social Media Growth & Content",
      desc: "Strategic social media content creation, brand positioning, audience engagement, and channel growth management.",
      highlights: ["Brand Positioning", "Content Strategy", "Channel Growth"]
    },
    {
      icon: <BarChart3 className="text-amber-500 dark:text-amber-400" size={26} />,
      title: "Conversion Rate Optimization (CRO)",
      desc: "A/B landing page testing, heatmaps analysis, funnel optimization, and user journey enhancements to maximize lead generation.",
      highlights: ["A/B Page Testing", "Heatmap Analysis", "Funnel Optimization"]
    },
    {
      icon: <TrendingUp className="text-emerald-600 dark:text-emerald-400" size={26} />,
      title: "Brand Strategy & Positioning",
      desc: "Crafting unique brand narratives, market differentiation, competitor benchmarking, and go-to-market execution.",
      highlights: ["Brand Narrative", "Go-To-Market", "Market Benchmark"]
    },
    {
      icon: <Award className="text-rose-600 dark:text-rose-400" size={26} />,
      title: "Analytics & ROI Reporting",
      desc: "Transparent GA4 event tracking, Google Tag Manager setups, and real-time executive performance reporting dashboards.",
      highlights: ["GA4 Event Tracking", "GTM Tagging", "Realtime Dashboards"]
    }
  ];

  const toolsStackData = [
    {
      name: "Google Analytics 4",
      category: "Traffic & Event Analytics",
      icon: React.createElement(SiGoogleanalytics as any, { className: "text-[#E37400]", size: 32 })
    },
    {
      name: "Meta Ads Manager",
      category: "Facebook & Instagram Ads",
      icon: React.createElement(SiMeta as any, { className: "text-[#0081FB]", size: 32 })
    },
    {
      name: "Google Search Console",
      category: "SEO Indexing & Keywords",
      icon: React.createElement(SiGooglesearchconsole as any, { className: "text-[#4285F4]", size: 32 })
    },
    {
      name: "SEMrush",
      category: "Competitor & SERP Insights",
      icon: React.createElement(SiSemrush as any, { className: "text-[#FF642D]", size: 32 })
    },
    {
      name: "Ahrefs",
      category: "Backlink & Keyword Audit",
      icon: (
        <svg viewBox="0 0 24 24" className="w-8 h-8 fill-[#0060FF]" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 22h4.5l2-4h7l2 4H22L12 2zm0 6.5L14.2 13H9.8L12 8.5z" />
        </svg>
      )
    },
    {
      name: "HubSpot",
      category: "CRM & Inbound Leads",
      icon: React.createElement(SiHubspot as any, { className: "text-[#FF7A59]", size: 32 })
    },
    {
      name: "Google Tag Manager",
      category: "Conversion Tagging",
      icon: React.createElement(SiGoogletagmanager as any, { className: "text-[#246FDB]", size: 32 })
    }
  ];

  const marketingTypes = [
    {
      title: "WhatsApp & Conversational Marketing",
      icon: <MessageSquare className="text-emerald-600 dark:text-emerald-400" size={28} />,
      overview: "WhatsApp is the default business communication channel in India. Decision-makers prefer immediate chat interactions over lengthy email back-and-forth.",
      whyWorks: "Near-100% open rates and instant response times for qualification and appointment booking.",
      strategies: [
        "Click-to-WhatsApp Ads: Meta/Google ads routing directly to a WhatsApp Business API chatbot for quick requirement collection.",
        "Interactive Portfolio PDF/Brochures: Sending compact, mobile-friendly 1-page PDF portfolios and price breakdowns directly on chat.",
        "Automated Lead Follow-ups: Instant automated replies when a quote form is filled on your website, offering an immediate phone or Zoom demo slot."
      ]
    },
    {
      title: "Localized SEO & Google Business Profile (GBP)",
      icon: <MapPin className="text-blue-600 dark:text-cyan-400" size={28} />,
      overview: "Indian business owners frequently look for technology partners within their city or state for easier communication and on-site alignment.",
      whyWorks: "Captures high-intent commercial searches like 'software development company in [City]' or 'WooCommerce developer near me'.",
      strategies: [
        "City-Specific Landing Pages: Pages optimized for regional tech hubs (e.g., 'Custom ERP & CRM Development Services in Raipur/Hyderabad').",
        "Google Business Profile Optimization: Regular updates with office photos, project milestone announcements, and active client review management.",
        "Localized Problem-Solving Blogs: Content addressing Indian business frameworks, such as GST e-invoicing integrations or Indian payment gateway setups (Razorpay, Cashfree)."
      ]
    },
    {
      title: "Performance Marketing with Direct-Action CTAs (Google & Meta Ads)",
      icon: <Zap className="text-amber-500 dark:text-amber-400" size={28} />,
      overview: "Paid search captures active buyers, while Meta ads (Facebook/Instagram) generate demand among traditional business owners modernizing their operations.",
      whyWorks: "Delivers rapid pipeline growth by targeting specific business owners and technology leads searching for urgent tech execution.",
      strategies: [
        "Google Search Ads: Bidding on high-intent phrases like 'hire dedicated PHP/React developer' or 'enterprise web app development company'.",
        "Direct-Action Ad Copy: Highlighting fixed pricing or turnaround speed (e.g., 'Custom Mobile App Delivered in 4 Weeks – Free Architecture Consultation').",
        "Lead Generation Forms with Call/WhatsApp Buttons: Bypassing complex forms to collect phone numbers directly on Meta platforms."
      ]
    },
    {
      title: "LinkedIn Outreach & Founder Personal Branding",
      icon: <Linkedin className="text-indigo-600 dark:text-violet-400" size={28} />,
      overview: "Indian startup founders, CTOs, and MSME executives use LinkedIn actively for vendor discovery and professional networking.",
      whyWorks: "Establishes credibility and bypasses gatekeepers for direct B2B conversations.",
      strategies: [
        "Behind-the-Scenes Build Breakdowns: Short posts explaining how your team solved a technical bottleneck or scaled an app’s database.",
        "Direct InMail Consultations: Focused, non-spammy outreach offering a free 15-minute code/UI audit for growing companies.",
        "Founder Commentary: Posts analyzing tech trends in India (e.g., ONDC adoption, UPI integrations, AI tools for SMEs)."
      ]
    },
    {
      title: "Video & Short-Form Visual Marketing (YouTube & LinkedIn Video)",
      icon: <Video className="text-rose-600 dark:text-rose-400" size={28} />,
      overview: "Visual demonstrations build trust much faster than abstract pitch decks. Indian clients want proof of capability before paying advances or signing retainers.",
      whyWorks: "Visual proof converts hesitant buyers into high-ticket clients faster.",
      strategies: [
        "Product & Dashboard Teardowns: 60–90 second screen recordings demonstrating live UI/UX, CRM portals, or e-commerce features built by your team.",
        "YouTube Problem-Solving Tutorials: Short videos answering common client queries (e.g., 'Cost of building a custom booking app in India').",
        "Client Milestone Videos: Short testimonial clips of business owners talking about how your software improved their daily operations."
      ]
    },
    {
      title: "B2B Directory & Marketplace Listings (IndiaMART, TradeIndia, Justdial)",
      icon: <Store className="text-cyan-600 dark:text-cyan-300" size={28} />,
      overview: "While often overlooked by modern tech agencies, traditional Indian MSMEs and manufacturing clients actively source IT vendors through established domestic B2B directories.",
      whyWorks: "Captures traditional Indian business owners searching for local IT development contracts.",
      strategies: [
        "Verified B2B Directory Profiles: Complete profiles on IndiaMART, TradeIndia, and Justdial highlighting custom software, websites, and mobile app services.",
        "Direct Inquiry Response System: Rapid response setup to convert inbound directory leads via direct call or WhatsApp within 5 minutes.",
        "Client Testimonials & Badges: Displaying verified directory ratings and client reviews to build instant trust."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-sans py-12 md:py-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* HERO */}
        <div className="text-center max-w-4xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
            <Sparkles size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Data-Driven Growth
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight">
            🚀 Digital Marketing & <span className="text-blue-600 dark:text-cyan-400">SEO Growth</span>
          </h1>

          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg max-w-2xl mx-auto font-medium leading-relaxed">
            Accelerate your business with data-backed SEO campaigns, targeted Meta & Google Ads, and brand growth strategies designed to maximize sales and ROI.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Link 
              to="/enquirynow" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 !text-white shadow-lg shadow-blue-600/25 transition-all cursor-pointer"
            >
              <Rocket size={18} className="!text-white" />
              <span className="!text-white font-bold">Request Growth Audit</span>
              <ArrowRight size={16} className="!text-white" />
            </Link>

            <Link 
              to="/Meeting" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:border-blue-500 transition-all cursor-pointer shadow-sm"
            >
              <Calendar size={18} />
              <span>Book Growth Call</span>
            </Link>
          </div>
        </div>

        {/* TYPES OF DIGITAL MARKETING STRATEGIES SECTION */}
        <div className="space-y-8">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3.5 py-1.5 rounded-full border border-blue-200 dark:border-blue-500/20">
              Growth Playbook
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Types of Digital Marketing Strategies
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">
              Proven, high-converting digital marketing channels tailored for modern businesses, startups, and enterprise growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {marketingTypes.map((type, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -6, scale: 1.01 }}
                transition={{ duration: 0.2 }}
                className="group relative overflow-hidden backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 border border-slate-200/90 dark:border-slate-800/90 rounded-3xl p-7 space-y-6 shadow-xl hover:shadow-2xl hover:border-blue-500/50 dark:hover:border-cyan-500/50 transition-all flex flex-col justify-between"
              >
                {/* BLURRY BACKDROP GLOW DECORATION */}
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-blue-500/10 dark:bg-cyan-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform pointer-events-none" />

                <div className="space-y-4 relative z-10">
                  {/* ANIMATED FLOATING ICON */}
                  <div className="flex items-center gap-3.5">
                    <motion.div 
                      animate={{ scale: [1, 1.06, 1] }}
                      transition={{ repeat: Infinity, duration: 4, ease: "easeInOut", delay: idx * 0.2 }}
                      className="w-13 h-13 p-3 rounded-2xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-md shrink-0 group-hover:scale-110 transition-transform"
                    >
                      {type.icon}
                    </motion.div>
                    <h3 className="text-lg font-extrabold text-slate-900 dark:text-white leading-snug group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                      {type.title}
                    </h3>
                  </div>

                  <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed font-medium">
                    {type.overview}
                  </p>

                  {/* WHY IT WORKS BADGE */}
                  <div className="p-3 rounded-2xl bg-blue-50/80 dark:bg-blue-950/40 border border-blue-200/80 dark:border-blue-800/60 space-y-1">
                    <span className="text-[11px] font-bold text-blue-700 dark:text-cyan-300 flex items-center gap-1">
                      💡 Why It Works:
                    </span>
                    <p className="text-[11px] text-slate-700 dark:text-slate-300 font-medium leading-normal">
                      {type.whyWorks}
                    </p>
                  </div>
                </div>

                {/* BEST CONTENT & STRATEGY */}
                <div className="space-y-2.5 pt-4 border-t border-slate-100 dark:border-slate-800/80 relative z-10">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
                    ⚡ Best Content & Strategy:
                  </span>
                  <div className="space-y-2">
                    {type.strategies.map((strat, sIdx) => (
                      <div key={sIdx} className="flex items-start gap-2 text-xs font-medium text-slate-700 dark:text-slate-300">
                        <CheckCircle2 size={14} className="text-blue-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                        <span className="leading-snug">{strat}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* VISUAL GROWTH ANALYTICS & AD TECH STACK GRID */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-500/20">
              Marketing Technology Stack
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Growth Analytics & Ad Tech Stack
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
              We leverage industry-leading analytics platforms, ad networks, and SEO tools for maximum ROI.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {toolsStackData.map((tool, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ scale: 1.04, y: -2 }}
                className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 p-4 rounded-2xl flex flex-col items-center justify-center text-center space-y-2.5 transition-all shadow-sm hover:shadow-md hover:border-blue-500/40 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-sm">
                  {tool.icon}
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-slate-900 dark:text-white leading-tight">{tool.name}</h4>
                  <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 block mt-0.5">{tool.category}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CAPABILITIES GRID WITH ANIMATED ICON BADGES */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-500/20">
              Core Capabilities
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Marketing & SEO Capabilities
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
              Strategic, performance-focused marketing services engineered for organic & paid channel dominance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ y: -6, scale: 1.01 }}
                transition={{ duration: 0.2 }}
                className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-7 space-y-5 shadow-lg hover:shadow-2xl hover:border-blue-500/50 dark:hover:border-cyan-500/50 transition-all flex flex-col justify-between cursor-pointer"
              >
                <div className="space-y-4">
                  {/* ANIMATED FLOATING ICON BADGE */}
                  <motion.div 
                    animate={{ y: [0, -4, 0] }}
                    transition={{ repeat: Infinity, duration: 3, ease: "easeInOut", delay: idx * 0.2 }}
                    className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/60 dark:to-cyan-950/60 border border-blue-200/80 dark:border-cyan-500/30 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform"
                  >
                    {cap.icon}
                  </motion.div>

                  <div className="space-y-2">
                    <h3 className="text-xl font-extrabold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                      {cap.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed font-medium">
                      {cap.desc}
                    </p>
                  </div>

                  {/* HIGHLIGHT FEATURE CHIPS */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {cap.highlights.map((item, hIdx) => (
                      <span key={hIdx} className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800/80 text-[11px] font-bold text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700/80">
                        🚀 {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800/80">
                  <Link to="/enquirynow" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-cyan-400 hover:text-blue-700 dark:hover:text-cyan-300 group/link">
                    <span>Enquire For Campaign</span>
                    <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA BANNER */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-900/90 dark:to-indigo-950/90 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-2xl">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Ready to Scale Your Online Revenue?
          </h2>
          <p className="text-blue-100 max-w-xl mx-auto text-sm font-medium">
            Contact our growth team for a complimentary SEO & website audit. We'll outline custom campaigns tailored to your target audience.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link 
              to="/enquirynow" 
              className="h-12 px-8 rounded-xl font-bold text-sm bg-white text-blue-700 hover:bg-slate-100 shadow-md flex items-center gap-2 cursor-pointer"
            >
              <span>Get Marketing Proposal</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
